import { Assessment, Recommendations, User } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc } from "firebase/firestore";

export type RecommendationsState = {
  recommendations: null | Recommendations;
  userSelection: null | Recommendations;
  view: "recommendations" | "reports";
  // open: boolean;
  report: null | Report;
  updated: boolean;
};

const initialState: RecommendationsState = {
  recommendations: null,
  userSelection: null,
  view: "recommendations",
  // open: false,
  report: null,
  updated: false,
};

export type Report = {
  full_name: string;
  pronouns: string;
  dob: string;
  home_address: string;
  work_address: string;
  phone: string;
  email: string;
  time_to_contact: string;
  contact_name: string;
  contact_relationship: string;
  contact_phone: string;
  contact_email: string;
  employment_status: string;
  company_name: string;
  role: string;
  job_description: string;
  start_date: string;
  working_hours: string;
  working_days: string[];
  hybrid_worker: string;
  work_contact_name: string;
  work_contact_job_title: string;
  work_contact_email: string;
  work_contact_phone: string;
  utr_number: string;
  conditions: string[];
  conditions_extra: string[];
  support_worker: Recommendations["support_worker"];
};

type SetReportAction = {
  name: string;
  email: string;
  answers: Assessment;
  support_worker: Recommendations["support_worker"];
};

const recommendationsSlice = createSlice({
  name: "recommendations",
  initialState,
  reducers: {
    setRecommendations: (
      state,
      action: PayloadAction<RecommendationsState["recommendations"]>
    ) => {
      state.recommendations = action.payload;
    },
    setUserSelection: (state, action: PayloadAction<Recommendations>) => {
      state.userSelection = action.payload;

      const localData = JSON.parse(localStorage.getItem("sita_user") ?? "{}");

      localStorage.setItem(
        "sita_user",
        JSON.stringify({
          ...localData,
          recommendations: action.payload,
        })
      );
    },
    setView: (state, action: PayloadAction<RecommendationsState["view"]>) => {
      state.view = action.payload;
    },
    setReport: (state, action: PayloadAction<SetReportAction>) => {
      const user = action.payload;

      state.report = {
        full_name: user.name,
        pronouns: user.answers?.about_you?.pronouns ?? "",
        dob: user.answers?.about_you?.dob ?? "",
        home_address: `${user.answers?.about_you?.address ?? ""} ${
          user.answers?.about_you?.address_2 ?? ""
        } ${user.answers?.about_you?.city ?? ""} ${
          user.answers?.about_you?.postcode ?? ""
        }`,
        work_address: user.answers?.about_your_job?.work_address ?? "",
        phone: user.answers?.about_you?.phone ?? "",
        email: user?.email ?? "",
        time_to_contact: user.answers?.about_you?.time_to_contact ?? "",
        contact_name: user.answers?.about_you?.thid_party_full_name ?? "",
        contact_relationship:
          user.answers?.about_you?.third_party_relationship ?? "",
        contact_phone: user.answers?.about_you?.third_party_phone ?? "",
        contact_email: user.answers?.about_you?.third_party_email ?? "",
        employment_status:
          user.answers?.about_your_job?.employment_status ?? "",
        company_name: user.answers?.about_your_job?.company ?? "",
        role: user.answers?.about_your_job?.role ?? "",
        job_description: user.answers?.about_your_job?.job_description ?? "",
        start_date: user.answers?.about_your_job?.start_date ?? "",
        working_hours: user.answers?.about_your_job?.hours ?? "0",
        working_days: user.answers?.about_your_job?.days ?? [],
        hybrid_worker: user.answers?.about_your_job?.hybrid_worker ?? "",
        work_contact_name:
          user.answers?.about_your_job?.work_contact_name ?? "",
        work_contact_job_title:
          user.answers?.about_your_job?.work_contact_job_title ?? "",
        work_contact_email:
          user.answers?.about_your_job?.work_contact_email ?? "",
        work_contact_phone:
          user.answers?.about_your_job?.work_contact_phone ?? "",
        utr_number: user.answers?.about_your_job?.utr_number ?? "",
        conditions: user.answers?.about_you?.conditions ?? [],
        conditions_extra: user.answers?.about_you?.conditions_extra ?? [],
        support_worker: user.support_worker,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateDBRecommendations.pending, () => {})
      .addCase(updateDBRecommendations.fulfilled, (state) => {
        state.updated = true;
      });
  },
});

type UpdateDBRecommendationsData = {
  id: string;
  recommendations: Recommendations;
  activity?: boolean;
};

export const updateDBRecommendations = createAsyncThunk(
  "user/updateDBRecommendations",
  async (data: UpdateDBRecommendationsData) => {
    let recommendations = data.recommendations;

    const docRef = doc(collections.users, data.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let dbData = docSnap.data();

      if (!dbData.recommendations && data.activity) {
        dbData.activity = [
          ...dbData.activity,
          { title: `Selected recommendations`, timestamp: Date.now() },
        ];
      }

      let updatedData: User = {
        ...dbData,
        recommendations: recommendations,
        last_saved: Date.now(),
      };

      try {
        await setDoc(docRef, updatedData, { merge: true });
        // update localStorage

        let localData = JSON.parse(localStorage.getItem("sita_user") ?? "{}");

        if (localData) {
          if (!localData.designated_helper) {
            localData.activity = updatedData.activity;
          }

          localStorage.setItem(
            "sita_user",
            JSON.stringify({ ...localData, recommendations: recommendations })
          );
        }

        return data;
      } catch (e) {
        console.log("error saving to db", e);
        return false;
      }
    }
  }
);

export const { setRecommendations, setUserSelection, setView, setReport } =
  recommendationsSlice.actions;

export default recommendationsSlice.reducer;
